import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { ApiDigitalAuthMember } from '../../../services/auth/member.service';
import { CompanyMember } from '../../../models/member.model';
import { modalShow } from '../../../utils/modal.util';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppPreloader from '../../../components/Preloader';
import AppModalNotificationInvitation from '../../../components/modal/ModalInvitationCreate';

export interface MemberListPageProps {};

const INVITATION_ARN = 'digital:auth:entity:invitation:*'

const MemberListPage: React.FunctionComponent<MemberListPageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()
  const { can: canSub } = useSubscription()

  const [mounted, setMounted] = useState(false)
  const [members, setMembers] = useState<CompanyMember[] | null>(null)

  const inviteAccess = canSub('create', INVITATION_ARN)
  const showInviteButton = inviteAccess.allowed || inviteAccess.reason !== 'role'
  const disableInviteButton = !inviteAccess.allowed && inviteAccess.reason === 'status'

  const handleInvite = () => {
    if (disableInviteButton) return
    modalShow('modal-invitation-create')
  }

  const handleInvitationSent = useCallback(() => {
    if (appServiceForUser) {
      loadMembers(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  const loadMembers = async (api_key: string) => {
    await ApiDigitalAuthMember.getCompanyMembers(api_key).then((response) => {
      if (response.status === 200 || response.status === 304) {
        setMembers(Array.isArray(response.data) ? response.data : [])
      } else {
        setMembers([])
        errorWebHandler(lang, {response})
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
      setMembers([])
    })
  }

  useEffect(() => {
    if (appServiceForUser && companyForUser) {
      setMembers(null)
      loadMembers(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.users}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { members !== null
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h3 className="fw-bolder">{lang.labels.users}</h3>
            </div>
            {showInviteButton && (
              <div className="card-toolbar">
                <button
                  className={`btn btn-primary${disableInviteButton ? ' opacity-50' : ''}`}
                  type="button"
                  disabled={disableInviteButton}
                  title={disableInviteButton ? lang.labels.subscriptionExpiredAction : undefined}
                  onClick={handleInvite}
                >
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="black" />
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.invite}
                  </div>
                </button>
              </div>
            )}
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px sorting">{lang.labels.user}</th>
                      <th className="min-w-200px sorting">{lang.labels.email}</th>
                      <th className="min-w-200px sorting">{lang.labels.assignedRoles}</th>
                      <th className="w-75px sorting_disabled text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { members.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { members.map((item, index) => (
                        <tr key={index} className="row-dashed">
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                <div className="symbol-label fs-1 bg-light-primary text-primary">
                                  {(item.first_name || item.email).charAt(0).toUpperCase()}
                                </div>
                              </div>
                              <div className="d-flex flex-column">
                                <span className="fw-bolder text-dark">
                                  {item.first_name || item.last_name
                                    ? `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim()
                                    : item.email
                                  }
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{item.email}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {item.roles && item.roles.length > 0
                                ? item.roles.map((role, ri) => (
                                  <span key={ri} className={`badge ${role.is_system ? 'badge-light-info' : 'badge-light-primary'} fs-8`}>
                                    {role.name}
                                    {role.is_system && <span className="ms-1 text-info">({lang.labels.system})</span>}
                                  </span>
                                ))
                                : <small className="text-muted">{lang.labels.none}</small>
                              }
                            </div>
                          </td>
                          <td>
                            <div className="d-flex justify-content-end">
                              <Link to={`/main/admin/users/${item.id}`} className="btn btn-sm btn-icon btn-light-primary">
                                <span className="svg-icon svg-icon-3">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"/>
                                    <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM6.5 15C5.1 15 4 16.1 4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15Z" fill="black"/>
                                  </svg>
                                </span>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={4} className="text-center">{lang.labels.noMembers}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalNotificationInvitation appServiceForUser={appServiceForUser} companyForUser={companyForUser} onInvitationSent={handleInvitationSent}></AppModalNotificationInvitation>
    </div>
  )
};

export default MemberListPage;
